import { useEffect, useState } from 'react';
import { Plus, Download, Check, X, ClipboardPaste } from 'lucide-react';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { communityQuestionsService } from '../services/communityQuestionsService';
import type { CommunityQuestion } from '../types/community-questions';
import { QuestionCard } from './QuestionCard';
import { EditQuestionModal } from './EditQuestionModal';

export const QuestionsList = () => {
  const [questions, setQuestions] = useState<CommunityQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [duplicateQuestion, setDuplicateQuestion] = useState<CommunityQuestion | undefined>(undefined);
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());
  const [pasteJsonStatus, setPasteJsonStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const loadQuestions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await communityQuestionsService.getAllQuestions();
      // Sort by created date, newest first
      const sortedData = [...data].sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      setQuestions(sortedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const handleDelete = async (questionId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this question?');
    if (!confirmed) return;

    try {
      await communityQuestionsService.deleteQuestion(questionId);
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      // Remove the deleted question from selection
      setSelectedQuestionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete question');
    }
  };

  const handleEdit = (updatedQuestion: CommunityQuestion) => {
    // Update the question in the list without refetching
    setQuestions((prev) =>
      prev.map((q) => (q.id === updatedQuestion.id ? updatedQuestion : q))
    );
  };

  const handleCreateSuccess = (newQuestion: CommunityQuestion) => {
    setIsCreateModalOpen(false);
    setDuplicateQuestion(undefined);

    // Add new question to the top of the list without refetching
    setQuestions((prev) => [newQuestion, ...prev]);
  };

  const handleDuplicate = (questionId: string) => {
    const questionToDuplicate = questions.find(q => q.id === questionId);
    if (questionToDuplicate) {
      setDuplicateQuestion(questionToDuplicate);
      setIsCreateModalOpen(true);
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setDuplicateQuestion(undefined);
  };

  const handleToggleSelection = (questionId: string) => {
    setSelectedQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedQuestionIds.size === questions.length) {
      // Deselect all
      setSelectedQuestionIds(new Set());
    } else {
      // Select all
      setSelectedQuestionIds(new Set(questions.map(q => q.id)));
    }
  };

  const handleExportToJson = async () => {
    try {
      // Export selected questions, or all if none selected
      const questionsToExport = selectedQuestionIds.size > 0
        ? questions.filter(q => selectedQuestionIds.has(q.id))
        : questions;

      const jsonString = JSON.stringify(questionsToExport, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setExportStatus('success');
      setTimeout(() => setExportStatus('idle'), 2000);
    } catch {
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 2000);
    }
  };

  const handleCreateFromJson = async () => {
    try {
      // Read from clipboard
      const clipboardText = await navigator.clipboard.readText();

      // Try to parse as JSON
      const parsedJson = JSON.parse(clipboardText);

      // Validate it has the required fields for a question
      if (!parsedJson.text || !parsedJson.type || !parsedJson.options || !parsedJson.optionsOrder) {
        setPasteJsonStatus('error');
        setTimeout(() => setPasteJsonStatus('idle'), 2000);
        return;
      }

      // Create a clean duplicate without id, dates, and response counts
      const cleanQuestion: Partial<CommunityQuestion> = {
        text: parsedJson.text,
        description: parsedJson.description,
        learning: parsedJson.learning,
        type: parsedJson.type,
        optionsOrder: parsedJson.optionsOrder,
        options: {} as Record<string, { label: string; isCorrect?: boolean; responseCount: number }>
      };

      // Clean the options - remove response counts
      if (parsedJson.options && typeof parsedJson.options === 'object') {
        for (const [key, value] of Object.entries(parsedJson.options)) {
          if (value && typeof value === 'object' && 'label' in value) {
            const optionValue = value as { label: string; isCorrect?: boolean; responseCount?: number };
            cleanQuestion.options![key] = {
              label: optionValue.label,
              isCorrect: optionValue.isCorrect,
              responseCount: 0 // Reset response count
            };
          }
        }
      }

      setPasteJsonStatus('success');
      setTimeout(() => setPasteJsonStatus('idle'), 2000);

      // Open modal with the cleaned question data
      setDuplicateQuestion(cleanQuestion as CommunityQuestion);
      setIsCreateModalOpen(true);
    } catch {
      setPasteJsonStatus('error');
      setTimeout(() => setPasteJsonStatus('idle'), 2000);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
        <Button onClick={loadQuestions}>Retry</Button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <>
        <div className="p-4">
          <EmptyState
            title="No questions found"
            description="There are no community questions in the database yet. Use the Import tab to upload questions from a CSV file, or create a new question manually."
          />
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
            >
              Create First Question
            </Button>
            <Button
              onClick={handleCreateFromJson}
              variant="secondary"
              icon={<ClipboardPaste className="w-4 h-4" />}
            >
              From JSON
            </Button>
          </div>
        </div>

        {/* Create Question Modal */}
        <EditQuestionModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          duplicateData={duplicateQuestion}
          onSuccess={(question, isEdit) => {
            if (isEdit) {
              handleEdit(question);
            } else {
              handleCreateSuccess(question);
            }
          }}
        />
      </>
    );
  }

  const allSelected = questions.length > 0 && selectedQuestionIds.size === questions.length;
  const someSelected = selectedQuestionIds.size > 0 && selectedQuestionIds.size < questions.length;

  return (
    <>
      <div className="p-4 space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Questions ({questions.length})
            </h2>
            {selectedQuestionIds.size > 0 && (
              <span className="text-sm text-gray-600">
                ({selectedQuestionIds.size} selected)
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              variant="primary"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
            >
              Create New
            </Button>
            <Button
              onClick={handleCreateFromJson}
              variant={pasteJsonStatus === 'success' ? 'primary' : 'secondary'}
              size="sm"
              icon={
                pasteJsonStatus === 'success' ? (
                  <Check className="w-4 h-4" />
                ) : pasteJsonStatus === 'error' ? (
                  <X className="w-4 h-4" />
                ) : (
                  <ClipboardPaste className="w-4 h-4" />
                )
              }
              title="Create new question from JSON in clipboard"
            >
              {pasteJsonStatus === 'success'
                ? 'Loaded!'
                : pasteJsonStatus === 'error'
                  ? 'Invalid'
                  : 'From JSON'}
            </Button>
            <Button
              onClick={handleExportToJson}
              variant={exportStatus === 'success' ? 'primary' : 'secondary'}
              size="sm"
              icon={
                exportStatus === 'success' ? (
                  <Check className="w-4 h-4" />
                ) : exportStatus === 'error' ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Download className="w-4 h-4" />
                )
              }
            >
              {exportStatus === 'success'
                ? 'Copied!'
                : exportStatus === 'error'
                  ? 'Failed'
                  : selectedQuestionIds.size > 0
                    ? `Copy ${selectedQuestionIds.size} to JSON`
                    : 'Copy All to JSON'}
            </Button>
          </div>
        </div>

        {/* Select All Checkbox */}
        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(input) => {
                if (input) {
                  input.indeterminate = someSelected;
                }
              }}
              onChange={handleSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {allSelected ? 'Deselect All' : someSelected ? 'Select All' : 'Select All'}
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {questions.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              isSelected={selectedQuestionIds.has(question.id)}
              onToggleSelection={handleToggleSelection}
            />
          ))}
        </div>
      </div>

      {/* Create Question Modal */}
      <EditQuestionModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        duplicateData={duplicateQuestion}
        onSuccess={(question, isEdit) => {
          if (isEdit) {
            handleEdit(question);
          } else {
            handleCreateSuccess(question);
          }
        }}
      />
    </>
  );
};
